import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PREFILL_KEY } from "@/lib/constants";
import { FalconMark } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
);

export default function AuthPage({ mode }) {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone_number: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const { data } = await api.post(isRegister ? "/auth/register" : "/auth/login", payload);
      login(data);
      toast.success(isRegister ? "Welcome to Falcon Finserv" : `Welcome back, ${data.user.name}`);
      const prefill = sessionStorage.getItem(PREFILL_KEY);
      navigate(data.user.role === "admin" ? "/admin" : prefill ? "/apply" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B132B]">
      <aside className="hidden lg:flex flex-col justify-between p-12 gold-mesh relative grain">
        <FalconMark />
        <div>
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">Your channel to every bank</p>
          <h1 className="mt-4 font-display text-4xl font-bold text-white leading-tight">One application. Every partner bank compared. One relationship manager.</h1>
          <p className="mt-4 text-slate-300 max-w-md">Falcon Finserv facilitates your loan with the right partner bank or NBFC — negotiating terms, collecting documents at your doorstep and tracking the file from Lead Captured to Disbursed.</p>
        </div>
        <p className="text-xs text-slate-500">Falcon Finserv · Direct Selling Agent · Partnered with all major banks, HFCs &amp; NBFCs</p>
      </aside>
      <main className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md bg-white rounded-2xl p-8 sm:p-10 shadow-2xl" data-testid={`${mode}-form-card`}>
          <div className="lg:hidden mb-6"><FalconMark boxed /></div>
          <h2 className="font-display text-2xl font-semibold text-[#0F172A]">{isRegister ? "Create your client account" : "Sign in to your portal"}</h2>
          <p className="mt-1 text-sm text-slate-500">{isRegister ? "New accounts start as clients. Admins are provisioned by Falcon." : "Clients and Falcon operations team."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {isRegister && (
              <>
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" required value={form.name} onChange={set("name")} placeholder="Aarav Mehta" className="mt-1.5 h-11" data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="phone">Mobile number</Label>
                  <Input id="phone" required value={form.phone_number} onChange={set("phone_number")} placeholder="+91 98765 43210" className="mt-1.5 h-11" data-testid="register-phone-input" />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-1.5 h-11" data-testid={`${mode}-email-input`} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="••••••••" className="mt-1.5 h-11" data-testid={`${mode}-password-input`} />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={busy} className="gold-btn w-full h-11 rounded-full text-base" data-testid={`${mode}-submit-button`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isRegister ? "Create account" : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>
          <Button type="button" variant="outline" onClick={googleLogin} className="w-full h-11 rounded-full" data-testid="google-login-button">
            <GoogleIcon /> <span className="ml-2">Continue with Google</span>
          </Button>

          <p className="mt-6 text-center text-sm text-slate-500">
            {isRegister ? "Already a client? " : "New to Falcon? "}
            <Link to={isRegister ? "/login" : "/register"} className="font-semibold text-[#E05E00] hover:underline" data-testid="auth-switch-link">
              {isRegister ? "Sign in" : "Create an account"}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
